import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  VStack,
  Heading,
  Text,
  useColorMode,
  SimpleGrid,
  Flex,
  useColorModeValue,
  GridItem,
  Image,
  Divider,
  Icon,
  Grid,
} from "@chakra-ui/react";
import webDesignImage from "../images/Web-design.jpg";
import hoverDesignImage from "../images/hover-web-design.png";
import webDevImage from "../images/full-stack-dev.jpg";
import hoverDevImage from "../images/hover-full-stack-dev.png";
import promptEng from "../images/Prompt-engineering.jpg";
import hoverPromptEng from "../images/hover-prompt-engineering.png";
import { SiOpenai } from "react-icons/si";
import { FaRobot } from "react-icons/fa";
import aiAutomationImage from "../images/ai-automation.jpg";
import hoverAiAutomationImage from "../images/hover-ai-automation.png";
import {
  FaHtml5,
  FaCss3Alt,
  FaJs,
  FaBootstrap,
  FaReact,
  FaNodeJs,
  FaFigma,
} from "react-icons/fa";
import {
  SiNextdotjs,
  SiFramer,
  SiExpress,
  SiMongodb,
  SiCplusplus,
  SiCsharp,
  SiAdobexd,
  SiApollographql,
} from "react-icons/si";

const Hero = () => {
  const bg = useColorModeValue("whiteAlpha.300", "gray.900");
  const textColor = useColorModeValue("gray.800", "whiteAlpha.900");
  const gridItemsBorderColor = useColorModeValue("gray.200", "gray.700");
  const color = useColorModeValue("gray.300", "gray.600");
  const iconColor = useColorModeValue("black", "white");

  const shadow = useColorModeValue("md", "lg");
  const overlayColor = useColorModeValue("blackAlpha.600", "blackAlpha.700");
  const { colorMode } = useColorMode();
  const heroRef = useRef(); // Ref to the Hero component

  // Define skill icons
  const skills = [
    { name: "HTML", icon: FaHtml5 },
    { name: "CSS3", icon: FaCss3Alt },
    { name: "JavaScript", icon: FaJs },
    { name: "Bootstrap", icon: FaBootstrap },
    { name: "ReactJS", icon: FaReact },
    { name: "NextJS", icon: SiNextdotjs },
    { name: "Framer", icon: SiFramer },
    { name: "C++", icon: SiCplusplus },
    { name: "C#", icon: SiCsharp },
    { name: "NodeJS", icon: FaNodeJs },
    { name: "ExpressJS", icon: SiExpress },
    { name: "MongoDB", icon: SiMongodb },
    { name: "React Native", icon: FaReact },
    { name: "Figma", icon: FaFigma },
    { name: "Adobe XD", icon: SiAdobexd },
    { name: "API Integration", icon: SiApollographql, label: "Restful API's" },
    //{ name: "OpenAI API / LLMs", icon: SiOpenai, category: "ai" },
    { name: "Prompt Engineering", icon: FaRobot, category: "ai" },
    { name: "AI Automation", icon: FaRobot, category: "ai" },
  ];

  const services = [
    {
      title: "Full Stack Development",
      description:
        "Building seamless cross-platform Full Stack applications.",
      image: webDevImage,
      hoverImage: hoverDevImage,
    },
    {
      title: "AI Integration & Automation",
      description:
        "Embedding AI workflows and automations in products.",
      image: aiAutomationImage,
      hoverImage: hoverAiAutomationImage,
    },
    {
      title: "Web Design",
      description:
        "Creating visually appealing and user-friendly web interfaces.",
      image: webDesignImage,
      hoverImage: hoverDesignImage,
    },
    {
      title: "Prompt Engineering",
      description:
        "Leveraging AI to generate innovative and creative digital art.",
      image: promptEng,
      hoverImage: hoverPromptEng,
    },
  ];

  return (
    <Box
      width="full"
      minH={{ base: "auto", md: "calc(100vh - 32px)" }} // Adjust height for mobile
      position="relative"
      borderRadius="xs"
      bg={colorMode === "dark" ? "gray.800" : "white"}
      overflow="auto"
      ref={heroRef}
      p={8}
    >
      <Box
        //className="main"
        position="relative"
        zIndex="2"
        height="full"
        width="full"
        justifyContent="center"
        alignItems="center"
      >
        <Flex justifyContent="space-between" alignItems="center" mb={8}>
          <Box>
            <Heading
              as="h1"
              size={{ base: "lg", md: "xl" }}
              color={textColor}
              textAlign={{ base: "center", md: "left" }}
            >
              Mohammad Abubakar
            </Heading>
            <Text
              fontSize="lg"
              color={textColor}
              textAlign={{ base: "center", md: "left" }}
            >
              Full Stack developer | UI/UX Designer | Prompt Engineer
            </Text>
            <Divider my={"4"} color={color} />
          </Box>
        </Flex>
        <Box
          position="relative"
          zIndex="2"
          height="full"
          width="full"
          display="flex"
          flexDirection="column"
          justifyContent="center"
        >
          <Heading size="md" mb={4} color={textColor}>
            About me
          </Heading>
          <Text
            fontSize={{ base: "xs", md: "sm", lg: "md" }}
            mb={10}
            color={textColor}
            textAlign={"justify"}
          >
          Results-driven and versatile full-stack developer with strong experience in building cross-platform solutions for web and mobile. I specialize in the MERN stack (MongoDB, Express.js, React.js, Node.js), Flutter, and modern JavaScript frameworks to deliver robust, scalable, and high-performing applications. In addition to core development, I design and build AI-powered automations and workflows—integrating APIs, LLMs, and third-party services to streamline processes and create smarter products. My work covers the full development lifecycle: planning, backend architecture, intuitive UI design, integration, testing, and deployment. I enjoy solving real-world problems with clean, reliable technology and thrive in environments where innovation, clarity, and quality truly matter. Let’s connect and turn your next idea into a working product.
          </Text>

          <Heading size="md" mb={4} color={textColor}>
            My Services
          </Heading>

          <SimpleGrid
            columns={{ base: 1, sm: 2, md: 3 }} // Defines the columns for different screen sizes
            spacing={2}
            autoFlow="row dense"
            gridTemplateColumns="repeat(auto-fit, minmax(250px, 1fr))" // Ensures grid items are of equal width and wrap as necessary
            minChildWidth="300px" // Sets the minimum width for grid items
            justifyContent="flex-start" // Aligns items to the start of the grid
            alignItems="flex-start" // Ensures items are aligned to the top of the grid
            mb={"10"}
          >
            {services.map((service, index) => (
              <GridItem
                key={index}
                bg={bg}
                borderWidth={"1px"}
                borderColor={gridItemsBorderColor}
                borderRadius="lg"
                boxShadow={shadow}
                overflow="hidden"
                position="relative"
                transition="transform 0.3s, box-shadow 0.3s"
                //transform="scale(0.9)" // Decreased the scale to make the tiles smaller
                _hover={{
                  transform: "scale(0.98)", // Scale up on hover
                  boxShadow: "xl",
                }}
              >
                <Box position="relative">
                  <Image
                    src={service.image}
                    alt={service.title}
                    objectFit="cover"
                    w="100%"
                    h={{ base: "150px", md: "200px", lg: "250px" }} // Reduced height for smaller appearance
                    zIndex="1"
                  />
                  <Flex
                    position="absolute"
                    top="0"
                    left="0"
                    width="100%"
                    height="100%"
                    bg={overlayColor}
                    align="center"
                    justify="center"
                    zIndex="2"
                    opacity="0"
                    transition="opacity 0.3s"
                    _hover={{ opacity: 1 }}
                  >
                    <Image
                      src={service.hoverImage}
                      alt={`${service.title} Hover`}
                      objectFit="cover"
                      w="100%"
                      h={{ base: "150px", md: "200px", lg: "250px" }}
                      position="absolute"
                      top="0"
                      left="0"
                      zIndex="3"
                      opacity="0"
                      transition="opacity 0.3s"
                      _hover={{ opacity: 1 }}
                    />
                  </Flex>
                </Box>
                <Box p={4} zIndex="0">
                  <Text
                    fontWeight="bold"
                    fontSize={{ base: "md", md: "lg", lg: "xl" }}
                    mb={2}
                    color={textColor}
                  >
                    {service.title}
                  </Text>
                  <Text color={textColor} fontSize={{ base: "sm", md: "md" }}>
                    {service.description}
                  </Text>
                </Box>
              </GridItem>
            ))}
          </SimpleGrid>

          <Heading size="md" mb={4} color={textColor}>
            My Skills
          </Heading>

          <Grid templateColumns="repeat(auto-fit, minmax(120px, 1fr))" gap={2}>
          {skills.map((skill, index) => {
              const isAi = skill.category === "ai";

              const SkillCard = (
                <Box
                  minWidth="120px"
                  maxWidth="200px"
                  w="100%"
                  p={{ base: 2, md: 4 }}
                  borderWidth={isAi ? "0px" : "1px"}
                  borderStyle="solid"
                  borderColor={isAi ? "transparent" : color}
                  borderRadius="lg"
                  transition="transform 0.3s, box-shadow 0.3s"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexDirection="column"
                  bg={colorMode === "dark" ? "gray.800" : "white"}
                >
                  <Flex alignItems="center" justifyContent="center">
                    <Icon
                      as={skill.icon}
                      boxSize={{ base: "16px", md: "24px" }} // Adjust sizes as needed
                      color={iconColor}
                    />
                    <Text
                      ml={2}
                      fontSize={"12"}
                      fontWeight={"medium"}
                      color={textColor}
                    >
                      {skill.label || skill.name}
                    </Text>
                  </Flex>
                </Box>
              );

              if (!isAi) {
                return <React.Fragment key={index}>{SkillCard}</React.Fragment>;
              }

              return (
                <Box
                  key={index}
                  minWidth="120px"
                  maxWidth="200px"
                  w="100%"
                  p="2px"
                  borderRadius="lg"
                  bg="linear-gradient(135deg, #7c3aed, #06b6d4)"
                >
                  {SkillCard}
                </Box>
              );
            })}
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default Hero;
